/**
 * Per-commitment trading-key nonce reservation, shared across every
 * nonce-consuming action (trade, deposit, withdraw, claim, redeem,
 * dispute - see `trader-crypto.js` `OP`).
 *
 * Why this exists: `prediction-market`'s contract only accepts the exact
 * next nonce for a trading commitment (`state.rs` `auth_trader`). Every
 * call site used to read `next_nonce` from the chain independently right
 * before signing, with no coordination - so two actions issued close
 * together from *different* components on the same page (e.g. trade
 * market A, then immediately trade market B, or trade then claim) could
 * both read the same on-chain value and collide, even though each
 * component's own "busy" flag only serializes submits *within* itself.
 *
 * This module serializes nonce reservation per commitment through a
 * promise chain, so concurrent callers on the same page always get
 * distinct, sequential nonces instead of racing on a shared chain read.
 * It does not (and cannot) prevent a race across separate browser tabs,
 * devices, or logins - callers should still handle a nonce-rejection
 * error from the chain as a fallback (see `trade-ticket.js`
 * `attemptTrade`'s retry).
 *
 * Confirmed empirically (see `prediction-market/tests/contract.rs`
 * `duplicate_trade_with_stale_nonce_second_call_rejected` and the
 * nonce-consumption check next to it) that a contract call which panics -
 * whether from a bad nonce (rejected before the nonce bump even runs) or
 * from anything else after auth succeeds - never advances the on-chain
 * nonce; Piecrust commits a call's state changes atomically, so a panic
 * anywhere in the call unwinds all of it. That's why `releaseNonce` below
 * is safe to just give a failed reservation back for reuse instead of
 * treating it as burned.
 */

const chains = new Map(); // commitmentHex -> Promise (tail of the reservation chain)
const localNext = new Map(); // commitmentHex -> next nonce this page has already reserved

/**
 * Reserves the next nonce for `commitmentHex`, reading the chain's current
 * value via `readChainNonce()` only when this page doesn't already have a
 * reservation ahead of it. Concurrent calls for the same commitment are
 * queued so each gets a distinct, increasing nonce.
 *
 * @param {string} commitmentHex
 * @param {() => Promise<number>} readChainNonce
 * @returns {Promise<number>}
 */
export function reserveNonce(commitmentHex, readChainNonce) {
  const prior = chains.get(commitmentHex) ?? Promise.resolve();
  const reserved = prior.then(async () => {
    const chainNonce = await readChainNonce();
    const floor = localNext.get(commitmentHex) ?? 0;
    const nonce = Math.max(chainNonce, floor);
    localNext.set(commitmentHex, nonce + 1);
    return nonce;
  });
  // Keep the chain alive even if this reservation's caller later fails -
  // only the reservation step itself (the read + bookkeeping above) needs
  // to have settled before the next queued caller proceeds.
  chains.set(
    commitmentHex,
    reserved.then(
      () => {},
      () => {}
    )
  );
  return reserved;
}

/**
 * Gives a reserved nonce back after its call failed (and therefore, per
 * the contract's atomic-revert behavior, never actually consumed it
 * on-chain) - so the next reservation reuses it instead of drifting ahead
 * of the real chain value. Only rewinds if `nonce` is still the most
 * recent reservation for this commitment; a failure from an older,
 * already-superseded reservation is a no-op (something newer is already
 * in flight and owns the pointer).
 *
 * @param {string} commitmentHex
 * @param {number} nonce
 */
export function releaseNonce(commitmentHex, nonce) {
  if (localNext.get(commitmentHex) === nonce + 1) {
    localNext.set(commitmentHex, nonce);
  }
}

/** Forces the next reservation to re-read from chain - used when a nonce-
 * rejection error surfaces despite reservation (cross-tab/device race). */
export function resyncNonce(commitmentHex) {
  localNext.delete(commitmentHex);
}

/**
 * Reserves a nonce, runs `attempt(nonce)`, and - only if `retryOnStaleNonce`
 * is true (see `trade-settings.js`, opt-in and off by default) - retries
 * once with a freshly reserved nonce if `attempt` fails with a
 * nonce-related error. This is purely a backstop for a cross-tab/device
 * race: same-page races are already prevented outright by `reserveNonce`
 * queueing every caller for a commitment, so a nonce error here means
 * something outside this page's queue (another tab, device, or login)
 * beat this reservation to the chain.
 *
 * When a nonce error occurs and retry is disabled, the nonce is still
 * released (safe - see `prediction-market/tests/contract.rs`
 * `failed_trade_after_successful_auth_does_not_consume_nonce`: a failed
 * call never actually consumes the on-chain nonce) and the thrown error is
 * annotated to say so, rather than just failing silently as "bad nonce"
 * with no explanation of what happened or what to do about it.
 *
 * @param {string} commitmentHex
 * @param {() => Promise<number>} readChainNonce
 * @param {(nonce: number) => Promise<T>} attempt
 * @param {{ retryOnStaleNonce?: boolean, onRetrying?: (e: Error) => void }} [opts]
 * @returns {Promise<T>}
 * @template T
 */
export async function withNonceRetry(
  commitmentHex,
  readChainNonce,
  attempt,
  { retryOnStaleNonce = false, onRetrying } = {}
) {
  const maxAttempts = retryOnStaleNonce ? 2 : 1;
  for (let attemptNum = 1; ; attemptNum++) {
    const nonce = await reserveNonce(commitmentHex, readChainNonce);
    try {
      return await attempt(nonce);
    } catch (e) {
      const isNonceError = /nonce/i.test(e?.message || "");
      releaseNonce(commitmentHex, nonce);
      if (!isNonceError) throw e;
      if (attemptNum >= maxAttempts) {
        throw new Error(
          retryOnStaleNonce
            ? `${e.message} (retried once, still stale - likely another tab/device just traded)`
            : `${e.message} - auto-retry is off, so this wasn't retried automatically. Enable it in Settings, or just try again.`
        );
      }
      resyncNonce(commitmentHex);
      onRetrying?.(e);
    }
  }
}
